import React, { useCallback, useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import {
  createItem,
  deleteItem,
  getList,
  updateItem,
} from "../../api/generics";
import { getAbsoluteUrl } from "../../api/urls";
import { fetchAuthenticated } from "../../api/utils";
import Document from "../../models/document";
import { SUCCESS } from "../../utils/constants/tags";
import { successAlert, warningAlert } from "../utils/messages";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./DocumentSpreadsheetCrud.css";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CPagination,
  CRow,
  CButton,
  CInputGroup,
  CInputGroupText,
  CInput,
} from "@coreui/react";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import Asset from "../../models/asset";
import Subcompany from "../../models/subcompany";
import DocumentCategory from "../../models/document-category";
import DocumentSubCategory from "../../models/document-subcategory";
import Tag from "../../models/tag";
import TagsCell from "./TagsCell";

const spanishLocaleText = {
  // Filter translations
  filterOoo: "Filtrar...",
  equals: "Igual",
  notEqual: "No igual",
  lessThan: "Menor que",
  greaterThan: "Mayor que",
  inRange: "En rango",
  contains: "Contiene",
  notContains: "No contiene",
  startsWith: "Comienza con",
  endsWith: "Termina con",
  andCondition: "Y",
  orCondition: "O",
  applyFilter: "Aplicar",
  resetFilter: "Reiniciar",
  clearFilter: "Limpiar",

  // Other common translations
  page: "Página",
  to: "a",
  of: "de",
  nextPage: "Siguiente página",
  lastPage: "Última página",
  firstPage: "Primera página",
  previousPage: "Página anterior",
  loadingOoo: "Cargando...",
  noRowsToShow: "Sin datos para mostrar",

  // translations for ag-Grid:
  // https://www.ag-grid.com/javascript-data-grid/localisation/
};
const idGenerator = (() => {
  let counter = -1;
  return {
    next: () => {
      return counter--;
    },
    reset: () => {
      counter = -1;
    },
  };
})();

const ITEMS_PER_PAGE = 10000;

const DocumentSpreadsheetCrud = () => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [documentCategoryId, setDocumentCategory] = useState<
    DocumentCategory[]
  >([]);
  const [, setSelectedCategoryId] = useState<number | null>(null);
  const [documentSubcategoryId, setDocumentSubcategory] = useState<
    DocumentSubCategory[]
  >([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [tags] = useState<Tag[]>([]);
  const [subcompany, setSubcompany] = useState<Subcompany[]>([]);
  const [, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addRowLoading, setAddRowLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingIds, setDownloadingIds] = useState<Set<number>>(new Set());
  const history = useHistory();
  const location = useLocation();
  const [gridApi, setGridApi] = useState<any>(null);
  const [changedCells, setChangedCells] = useState<{
    [key: number]: Set<string>;
  }>({});

  const [versionsModalVisible, setVersionsModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );
  const [documentVersions, setDocumentVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const isExpired = (expirationDate?: string): boolean => {
    if (!expirationDate) return false;
    const now = new Date();
    const expDate = new Date(expirationDate);
    return now > expDate;
  };
  // Import additional fields from Redux
  const additionalFields = useSelector(
    (state: RootState) => state.companyAdditionalFields.additionalFields,
  );

  const requiredFields = [
    { field: "name", message: "Nombre es obligatorio" },
    { field: "documentType", message: "Tipo de documento es obligatorio" },
    {
      field: "expirationNoticeDays",
      message: "Anticipación de vencimiento es obligatoria",
    },
    { field: "expirationGraceDays", message: "Tolerancia es obligatoria" },
    {
      field: "expirationReminderRepeatDays",
      message: "Repetición de alarma es obligatoria",
    },
  ];
  const [, setLastChangedCategory] = useState<{
    rowId: number;
    categoryId: number | null;
  } | null>(null);

  const onGridReady = (params: any) => {
    setGridApi(params.api);
  };

  useEffect(() => {
    if (gridApi) {
      gridApi.refreshCells({ force: true });
    }
  }, [gridApi, documents]);

  const [search, setSearch] = useState<string>("");
  const createNewDocument = (): Document => {
    const defaultAsset = assets.length > 0 ? assets[0] : undefined;
    const newDoc: any = {
      id: idGenerator.next(),
      name: "",
      subcompanyId: subcompany.length > 0 ? subcompany[0].id : undefined,
      documentCategoryId:
        documentCategoryId.length > 0 ? documentCategoryId[0].id : undefined,
      documentSubcategoryId: undefined,
      documentType: "digital",
      assetId: assets.length > 0 ? assets[0].id : undefined,
      typeName: defaultAsset?.typeName || "",
      physicalCopyRequired: false,
      expirationDate: undefined,
      expirationNoticeDays: 30,
      expirationGraceDays: 15,
      expirationReminderRepeatDays: 7,
      filePath: "",
      companyId: companyId,
    };

    // Initialize additional field values
    const activeFields = getActiveAdditionalFields();
    activeFields.forEach((field) => {
      newDoc[`fieldValue${field.index}`] = "";
    });

    return newDoc as Document;
  };

  const handleOpenVersionsModal = async (document: Document) => {
    if (!document.id || document.id < 0) {
      warningAlert(
        "El documento debe ser guardado primero para ver sus versiones.",
      );
      return;
    }

    setSelectedDocument(document);
    setVersionsLoading(true);
    setVersionsModalVisible(true);

    try {
      const versionsStatus = await getList(
        `/documents/${document.id}/versions/`,
        1000,
        0,
      );

      if (versionsStatus.status === SUCCESS && versionsStatus.data?.items) {
        setDocumentVersions(versionsStatus.data.items);
      } else {
        setDocumentVersions([]);
        if (versionsStatus.status !== SUCCESS) {
          warningAlert(
            versionsStatus.detail || "Error al cargar las versiones",
          );
        }
      }
    } catch (error) {
      console.error("Error loading versions:", error);
      warningAlert("Error al cargar las versiones del documento");
      setDocumentVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  };

  const handleCloseVersionsModal = () => {
    setVersionsModalVisible(false);
    setSelectedDocument(null);
    setDocumentVersions([]);
  };

  const handleDownloadVersion = async (versionId: string) => {
    if (!selectedDocument?.id) return;

    const downloadUrl = getAbsoluteUrl(
      `/documents/${selectedDocument.id}/get_document_url/?version=${versionId}`,
    );

    const downloadResponse = await fetchAuthenticated(downloadUrl, {
      method: "GET",
    });

    if (!downloadResponse.ok) {
      throw new Error(
        `Error HTTP ${downloadResponse.status}: ${downloadResponse.statusText}`,
      );
    }

    const blob = await downloadResponse.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = selectedDocument.filePath || "documento";

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    return () => {
      idGenerator.reset(); // Reset when component unmounts
    };
  }, []);
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id,
  );

  const getURLParams = () => {
    let urlParams = new URLSearchParams(location.search);
    let page = urlParams.get("page")
      ? parseInt(urlParams.get("page") as string)
      : 1;

    if (page <= 0) {
      page = 1;
    }
    let search = urlParams.get("search") ? urlParams.get("search") : "";
    let map = new Map();
    map.set("search", search);
    map.set("page", page);
    return map;
  };

  const fetchAssets = useCallback(async () => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", companyId!.toString());
    additionalParams.set("include_type", "true"); // Add this if your API supports it

    const assetsStatus = await getList<Asset>(
      "/assets/",
      1000,
      0,
      additionalParams,
    );

    if (assetsStatus.status === SUCCESS) {
      setAssets(assetsStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar los productores.");
      console.error("Error loading assets:", assetsStatus);
    }
  }, [companyId]);

  const fetchSubcompanies = useCallback(async () => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", companyId!.toString());
    const companiesStatus = await getList<Subcompany>(
      "/subcompanies/",
      1000,
      0,
      additionalParams,
    );
    if (companiesStatus.status === SUCCESS) {
      setSubcompany(companiesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las suborganizaciones.");
    }
  }, [companyId]);

  const fetchDocumentCategories = useCallback(async () => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", companyId!.toString());
    const documentcategoriesStatus = await getList<DocumentCategory>(
      "/document_categories/",
      1000,
      0,
      additionalParams,
    );
    if (documentcategoriesStatus.status === SUCCESS) {
      setDocumentCategory(documentcategoriesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las categorías de documentos.");
    }
  }, [companyId]);

  const fetchDocumentSubCategories = useCallback(async () => {
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("company_id", companyId!.toString());
    const documentSubcategoriesStatus = await getList<DocumentSubCategory>(
      "/document_subcategories/",
      1000,
      0,
      additionalParams,
    );
    if (documentSubcategoriesStatus.status === SUCCESS) {
      setDocumentSubcategory(documentSubcategoriesStatus.data!.items || []);
    } else {
      warningAlert("Error al cargar las subcategorías de documentos.");
    }
  }, [companyId]);

  const fetchDocuments = async () => {
    const urlParams = getURLParams();
    const limit = ITEMS_PER_PAGE;
    const offset = ITEMS_PER_PAGE * Number(urlParams.get("page") - 1);
    urlParams.set("company_id", companyId);

    const documentsStatus = await getList<Document>(
      "/documents/",
      limit,
      offset,
      urlParams,
    );

    if (documentsStatus.status === SUCCESS) {
      const count = documentsStatus.data?.count || 0;
      const pages = Math.ceil(count / ITEMS_PER_PAGE);
      setTotalPages(pages);

      // No intentar mapear el typeName aquí aún
      setDocuments(documentsStatus.data!.items || []);
      setCurrentPage(urlParams.get("page"));
      setChangedCells({});
    } else {
      warningAlert(documentsStatus.detail || "Error desconocido");
    }

    setLoading(false);
  };

  const handleAddNewRow = () => {
    setAddRowLoading(true);

    const newDocument = createNewDocument();
    setDocuments((prev) => [...prev, newDocument]);

    // Hacer scroll hasta la nueva fila
    setTimeout(() => {
      if (gridApi) {
        gridApi.ensureIndexVisible(documents.length, "bottom");
        const newNode = gridApi.getDisplayedRowAtIndex(documents.length);
        if (newNode) {
          gridApi.setFocusedCell(documents.length, "name");
          gridApi.startEditingCell({
            rowIndex: documents.length,
            colKey: "name",
          });
        }
      }
      setAddRowLoading(false);
    }, 500);
  };

  const handleSaveChanges = async () => {
    if (Object.keys(changedCells).length === 0) {
      warningAlert("No hay cambios para guardar.");
      return;
    }

    try {
      setLoading(true);
      const savePromises: Promise<any>[] = [];
      const tempIdMap: { [key: number]: number } = {};

      gridApi.forEachNode((node: any) => {
        if (changedCells[node.data.id]) {
          const docData = node.data;
          const { typeName, ...dataWithoutTypeName } = docData;

          for (let i = 1; i <= 10; i++) {
            if (
              dataWithoutTypeName[`fieldValue${i}`] !== null &&
              dataWithoutTypeName[`fieldValue${i}`] !== undefined
            ) {
              dataWithoutTypeName[`fieldName${i}`] = additionalFields
                ? additionalFields[
                    `fieldName${i}` as keyof typeof additionalFields
                  ]
                : undefined;
            }
          }

          if (docData.id < 0) {
            // CREATE operation for new documents
            const tempId = docData.id;
            const createData = {
              ...dataWithoutTypeName,
              companyId: companyId,
              id: undefined, // Ensure ID is not sent for new documents
            };

            savePromises.push(
              createItem<Document>("/documents/create/", createData).then(
                (response) => {
                  if (response.status !== SUCCESS) {
                    throw new Error(
                      response.detail || "Failed to create document",
                    );
                  }
                  if (response.data?.id !== undefined) {
                    tempIdMap[tempId] = response.data.id;
                  }
                  return { success: true, tempId, newId: response.data?.id };
                },
              ),
            );
          } else {
            // UPDATE operation for existing documents
            const { id, ...dataToSend } = dataWithoutTypeName;
            const updateData = {
              ...dataToSend,
              companyId: companyId,
            };

            savePromises.push(
              updateItem<Document>(`/documents/${id}/`, updateData).then(
                (response) => {
                  if (response.status !== SUCCESS) {
                    throw new Error(
                      response.detail || "Failed to update document",
                    );
                  }
                  return { success: true, id };
                },
              ),
            );
          }
        }
      });

      const results = await Promise.all(savePromises);

      // Update the documents with their new IDs
      if (Object.keys(tempIdMap).length > 0) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id !== undefined && doc.id in tempIdMap
              ? { ...doc, id: tempIdMap[doc.id] }
              : doc,
          ),
        );
      }

      if (results.every((r) => r.success)) {
        successAlert("Cambios guardados con éxito!");
        fetchDocuments(); // Refresh data
      }
    } catch (error) {
      console.error("Save operation failed:", error);
      warningAlert(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  };
  const [deletingIds, setDeletingIds] = useState<number[]>([]);
  const confirmDelete = (id: number) => {
    const document = documents.find((doc) => doc.id === id);
    const message = `¿Está seguro que desea eliminar el documento:\n\n"${
      document?.name || "Sin nombre"
    }"?\n\nEsta acción no se puede deshacer.`;

    if (window.confirm(message)) {
      handleDelete(id);
    }
  };
  const handleDelete = async (id: number) => {
    try {
      setDeletingIds((prev) => [...prev, id]);
      // For temporary IDs (negative), just remove from the local state
      if (id < 0) {
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));
        return;
      }

      // For real documents (positive IDs), perform the API call
      const documentToDelete = documents.find((doc) => doc.id === id);

      if (!documentToDelete) {
        warningAlert("Documento no encontrado.");
        return;
      }

      // Use the dedicated DELETE endpoint
      const deleteStatus = await deleteItem<Document>(
        `/documents/${id}/`,
        documentToDelete,
      );

      if (deleteStatus.status === SUCCESS) {
        successAlert("Documento eliminado con éxito!");

        // Update local state immediately for better UX
        setDocuments((prev) => prev.filter((doc) => doc.id !== id));

        // Then refresh data from server to ensure consistency
        fetchDocuments();
      } else {
        warningAlert(deleteStatus.detail || "Error al eliminar el documento.");
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      warningAlert("Error al eliminar el documento.");
    } finally {
      setDeletingIds((prev) => prev.filter((deletingId) => deletingId !== id));
    }
  };

  // Move handleFileUpload to top-level so it's accessible everywhere
  const handleFileUpload = async (
    file: File,
    documentId: number | undefined,
    documentData: Document,
  ) => {
    try {
      if (!file) throw new Error("No se seleccionó ningún archivo");

      // Crear FormData con el archivo y todos los datos del documento
      const formData = new FormData();

      if (documentId !== undefined && documentId !== null && documentId > 0) {
        formData.append("document_id", String(documentId));
      }

      // Agregar archivo
      formData.append("file", file);

      // Agregar datos del documento como form fields
      formData.append("name", documentData.name || file.name);
      formData.append("version", documentData.version || "1");
      formData.append("company_id", String(companyId));
      formData.append("file_path", file.name);
      formData.append("asset_id", String(documentData.assetId ?? ""));
      formData.append("document_type", documentData.documentType || "digital");
      formData.append(
        "physical_copy_required",
        String(documentData.physicalCopyRequired || false),
      );
      formData.append(
        "expiration_notice_days",
        String(documentData.expirationNoticeDays || 30),
      );
      formData.append(
        "expiration_grace_days",
        String(documentData.expirationGraceDays || 15),
      );
      formData.append(
        "expiration_reminder_repeat_days",
        String(documentData.expirationReminderRepeatDays || 7),
      );

      if (
        documentData.documentCategoryId !== null &&
        documentData.documentCategoryId !== undefined
      ) {
        formData.append(
          "document_category_id",
          String(documentData.documentCategoryId),
        );
      }
      if (
        documentData.documentSubcategoryId !== null &&
        documentData.documentSubcategoryId !== undefined
      ) {
        formData.append(
          "document_subcategory_id",
          String(documentData.documentSubcategoryId),
        );
      }
      if (
        documentData.subcompanyId !== null &&
        documentData.subcompanyId !== undefined
      ) {
        formData.append("subcompany_id", String(documentData.subcompanyId));
      }
      if (documentData.expirationDate) {
        formData.append("expiration_date", documentData.expirationDate);
      }
      // Construir URL absoluta y usar fetchAuthenticated
      const uploadUrl = getAbsoluteUrl("/documents/create_document_url/");
      const uploadResponse = await fetchAuthenticated(uploadUrl, {
        method: "POST",
        body: formData,
        // NO establecer Content-Type, FormData lo hace automáticamente
      });

      const result = await uploadResponse.json();

      if (!uploadResponse.ok || result.status !== SUCCESS) {
        throw new Error(result.detail || "Error al subir el archivo");
      }

      return true;
    } catch (error) {
      console.error("Error en handleFileUpload:", error);
      warningAlert(
        `Error al subir archivo: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
      return false;
    }
  };

  const onCellValueChanged = (event: any) => {
    const rowId = event.data.id;
    const field = event.colDef.field;

    setChangedCells((prev) => {
      const newChanges = { ...prev };
      if (!newChanges[rowId]) {
        newChanges[rowId] = new Set();
      }
      newChanges[rowId].add(field);
      return newChanges;
    });
  };
  const getRowClass = (params: any) => {
    const classes = [];

    if (changedCells[params.data.id] && changedCells[params.data.id].size > 0) {
      classes.push("row-changed");
    }

    // Marcar filas con campos obligatorios vacíos
    const hasEmptyRequiredField = requiredFields.some(
      (field) =>
        params.data[field.field] === undefined ||
        params.data[field.field] === null ||
        params.data[field.field] === "",
    );

    if (hasEmptyRequiredField) {
      classes.push("row-invalid");
    }

    return classes.join(" ");
  };

  // Helper function to get active additional fields
  const getActiveAdditionalFields = () => {
    if (!additionalFields) return [];

    const activeFields: Array<{
      index: number;
      name: string;
      type: string;
      choices?: string[];
    }> = [];

    for (let i = 1; i <= 10; i++) {
      const fieldName =
        additionalFields[`fieldName${i}` as keyof typeof additionalFields];
      const fieldType =
        additionalFields[`fieldType${i}` as keyof typeof additionalFields];
      const fieldChoices =
        additionalFields[`fieldChoices${i}` as keyof typeof additionalFields];

      if (fieldName && fieldType) {
        activeFields.push({
          index: i,
          name: fieldName as string,
          type: fieldType as string,
          choices: fieldChoices as string[] | undefined,
        });
      }
    }

    return activeFields;
  };

  // Generate dynamic columns for additional fields
  const generateAdditionalFieldColumns = (): ColDef[] => {
    const activeFields = getActiveAdditionalFields();

    return activeFields.map((field) => {
      const fieldKey = `fieldValue${field.index}`;

      return {
        field: fieldKey,
        headerName: field.name,
        editable: field.type !== "select" && field.type !== "checkbox", // Selects are handled by cellRenderer
        filter: true,
        width: 180,
        cellClassRules: {
          "cell-changed": (params: any) =>
            changedCells[params.data.id]?.has(fieldKey),
        },
        cellRenderer: (params: any) => {
          if (field.type === "select" && field.choices) {
            // Render as dropdown
            const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
              const updatedValue = e.target.value || null;
              params.setValue(updatedValue);

              setChangedCells((prev) => {
                const newChanges = { ...prev };
                if (!newChanges[params.data.id]) {
                  newChanges[params.data.id] = new Set();
                }
                newChanges[params.data.id].add(fieldKey);
                return newChanges;
              });
            };

            return (
              <select
                value={params.value || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "5px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              >
                <option value="">Seleccione una opción</option>
                {field.choices.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            );
          } else if (field.type === "checkbox") {
            // Render as checkbox
            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const updatedValue = e.target.checked ? "true" : "false";
              params.setValue(updatedValue);

              setChangedCells((prev) => {
                const newChanges = { ...prev };
                if (!newChanges[params.data.id]) {
                  newChanges[params.data.id] = new Set();
                }
                newChanges[params.data.id].add(fieldKey);
                return newChanges;
              });
            };

            return (
              <input
                type="checkbox"
                checked={params.value === "true" || params.value === true}
                onChange={handleChange}
                style={{
                  width: "16px",
                  height: "16px",
                  cursor: "pointer",
                }}
              />
            );
          } else if (field.type === "date") {
            // Render as date input
            const handleDateChange = (
              e: React.ChangeEvent<HTMLInputElement>,
            ) => {
              const updatedValue = e.target.value || null;
              params.setValue(updatedValue);

              setChangedCells((prev) => {
                const newChanges = { ...prev };
                if (!newChanges[params.data.id]) {
                  newChanges[params.data.id] = new Set();
                }
                newChanges[params.data.id].add(fieldKey);
                return newChanges;
              });
            };

            return (
              <input
                type="date"
                value={params.value || ""}
                onChange={handleDateChange}
                style={{
                  width: "100%",
                  padding: "0px",
                  border: "0px solid #ccc",
                  borderRadius: "0px",
                  backgroundColor: "transparent",
                }}
              />
            );
          } else if (field.type === "number") {
            // Render as number input in editable cell
            return params.value || "";
          } else if (field.type === "textarea") {
            // Render as text with wrapping
            return (
              <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {params.value || ""}
              </div>
            );
          } else {
            // Default text rendering
            return params.value || "";
          }
        },
        cellEditor:
          field.type === "number" ? "agNumberCellEditor" : "agTextCellEditor",
        cellEditorParams:
          field.type === "number" ? { precision: 2 } : undefined,
      } as ColDef;
    });
  };

  const columns: ColDef[] = [
    {
      field: "name",
      headerName: "Nombre",
      editable: true,
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some((f) => f.field === "name") &&
          (params.data.name === undefined ||
            params.data.name === null ||
            params.data.name === ""),
      },
    },
    {
      field: "assetId",
      headerName: "Productor",
      filter: "agTextColumnFilter",
      // Añadir este filterValueGetter para filtrar por nombre en lugar de ID
      filterValueGetter: (params) => {
        const assetId = params.data.assetId;
        const asset = assets.find((a) => a.id === assetId);
        return asset ? asset.name : "";
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const updatedValue = e.target.value
            ? parseInt(e.target.value, 10)
            : null;
          const selectedAsset = assets.find((a) => a.id === updatedValue);

          params.node.setDataValue("assetId", updatedValue);
          params.node.setDataValue("typeName", selectedAsset?.typeName || "");

          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[params.data.id]) {
              newChanges[params.data.id] = new Set();
            }
            newChanges[params.data.id].add("assetId");
            newChanges[params.data.id].add("typeName");
            return newChanges;
          });
        };

        return (
          <select
            value={params.value || ""}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            <option value="" disabled>
              Seleccione un Productor
            </option>
            {assets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      field: "subcompanyId",
      headerName: "Sub Organización",
      filter: "agTextColumnFilter",
      filterValueGetter: (params) => {
        const subId = params.data.subcompanyId;
        const sub = subcompany.find((s) => s.id === subId);
        return sub?.name || "";
      },
      filterParams: {
        // Corregido: tipo explícito para searchTerm
        textFormatter: (searchTerm: string) => {
          if (!searchTerm) return "";
          return searchTerm.toLowerCase();
        },
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const updatedValue = parseInt(e.target.value, 10);
          params.setValue(updatedValue);
          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[params.data.id]) {
              newChanges[params.data.id] = new Set();
            }
            newChanges[params.data.id].add("subcompanyId");
            return newChanges;
          });
        };

        return (
          <select
            value={params.value || ""}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            <option value="" disabled>
              Seleccione una suborganización
            </option>
            {subcompany.map((sub: Subcompany) => (
              <option key={sub.id} value={sub.id}>
                {sub.name || `Suborganización ${sub.id}`}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      field: "filePath",
      headerName: "Archivo subido",
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const downloadingDocument: Document = params.data;
        const filePath = params.value;
        const hasFile =
          filePath && typeof filePath === "string" && filePath.trim() !== "";
        const fileName = hasFile
          ? filePath.split("/").pop() || ""
          : "Sin archivo";
        const fileExtension = hasFile
          ? fileName.split(".").pop()?.toLowerCase()
          : "";

        const isDownloading = downloadingIds.has(downloadingDocument.id!);

        const getFileIcon = () => {
          if (!hasFile) return "fa fa-file";
          switch (fileExtension) {
            case "pdf":
              return "fa fa-file-pdf";
            case "doc":
            case "docx":
              return "fa fa-file-word";
            case "xls":
            case "xlsx":
              return "fa fa-file-excel";
            case "jpg":
            case "jpeg":
            case "png":
            case "gif":
              return "fa fa-file-image";
            default:
              return "fa fa-file";
          }
        };

        const handleDownload = (
          downloadingDocument: Document,
          e: React.MouseEvent,
        ) => {
          e.preventDefault();
          if (!hasFile || isDownloading) {
            return;
          }
          handleDownloadClick(downloadingDocument);
        };

        return (
          <div
            onClick={(e: React.MouseEvent) =>
              handleDownload(downloadingDocument, e)
            }
            style={{
              cursor: hasFile && !isDownloading ? "pointer" : "default",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "5px",
              opacity: isDownloading ? 0.7 : 1,
            }}
            title={
              isDownloading
                ? "Descargando..."
                : hasFile
                  ? `Descargar ${fileName}`
                  : "No hay archivo disponible"
            }
          >
            <i
              className={`${
                isDownloading ? "fa fa-spinner fa-spin" : getFileIcon()
              } ${hasFile ? "text-primary" : "text-secondary"}`}
            />
            <span
              style={{
                color: hasFile ? "#007bff" : "#6c757d",
                textDecoration: hasFile ? "underline" : "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "150px",
              }}
            >
              {isDownloading ? "Descargando..." : fileName}
            </span>
          </div>
        );
      },
    },
    {
      field: "versions",
      headerName: "Versiones",
      editable: false,
      filter: false,
      width: 120,
      cellRenderer: (params: any) => {
        const document = params.data;
        const hasVersions = document.id && document.id > 0; // Only for saved documents

        const handleViewVersions = () => {
          handleOpenVersionsModal(document);
        };

        return (
          <div style={{ textAlign: "center" }}>
            <button
              className="btn btn-outline-info btn-sm"
              onClick={handleViewVersions}
              disabled={!hasVersions}
              title={
                hasVersions ? "Ver versiones" : "Guardar documento primero"
              }
              style={{
                padding: "3px 6px",
                minWidth: "30px",
                opacity: hasVersions ? 1 : 0.5,
              }}
            >
              <i className="fa fa-history"></i>
            </button>
          </div>
        );
      },
    },
    {
      field: "filePath",
      filter: false,
      sortable: false,
      resizable: false,
      headerName: "Subir/Actualizar archivo",
      cellRenderer: (params: any) => {
        const handleFileInputChange = async (
          e: React.ChangeEvent<HTMLInputElement>,
        ) => {
          if (e.target.files && e.target.files.length > 0) {
            const file = e.target.files[0];
            const documentId = params.data.id;
            const documentData = params.data;
            const success = await handleFileUpload(
              file,
              documentId,
              documentData,
            );
            if (success) {
              // Optionally, update the filePath in the grid
              params.node.setDataValue("filePath", file.name);
              setChangedCells((prev: any) => {
                const newChanges = { ...prev };
                if (!newChanges[documentId]) {
                  newChanges[documentId] = new Set();
                }
                newChanges[documentId].add("filePath");
                return newChanges;
              });
            }
          }
        };

        return (
          <div style={{ textAlign: "center" }}>
            <label
              className="btn btn-outline-primary btn-sm"
              style={{
                padding: "3px 6px",
                minWidth: "30px",
                cursor: "pointer",
                margin: "0",
              }}
            >
              <i className="fa fa-upload"></i>Cargar archivo
              <input
                type="file"
                onChange={handleFileInputChange}
                style={{
                  display: "none",
                }}
              />
            </label>
          </div>
        );
      },
    },
    {
      field: "documentCategoryId",
      headerName: "Categoría de Documento",
      filter: "agTextColumnFilter",
      // 1. Obtener el nombre de la categoría para filtrar
      filterValueGetter: (params) => {
        const categoryId = params.data.documentCategoryId;
        const category = documentCategoryId.find((c) => c.id === categoryId);
        return category?.name || "";
      },
      filterParams: {
        // 2. Formatear el término de búsqueda (input del usuario)
        textFormatter: (searchTerm: string) => {
          return searchTerm.toLowerCase();
        },
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newCategoryId = parseInt(e.target.value, 10);
          const previousCategoryId = params.value;

          if (newCategoryId !== previousCategoryId) {
            params.setValue(newCategoryId);
            setLastChangedCategory({
              rowId: params.data.id,
              categoryId: newCategoryId,
            });
            params.node.setDataValue("documentSubcategoryId", null);

            setChangedCells((prev) => {
              const newChanges = { ...prev };
              if (!newChanges[params.data.id]) {
                newChanges[params.data.id] = new Set();
              }
              newChanges[params.data.id].add("documentCategoryId");
              return newChanges;
            });
          }
        };

        return (
          <select
            value={params.value || ""}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            <option value="" disabled>
              Seleccione categoría
            </option>
            {documentCategoryId.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      field: "documentSubcategoryId",
      headerName: "Subcategoría de Documento",
      filter: "agTextColumnFilter",
      // 1. Obtener el nombre de la subcategoría para filtrar
      filterValueGetter: (params) => {
        const subcategoryId = params.data.documentSubcategoryId;
        const subcategory = documentSubcategoryId.find(
          (s) => s.id === subcategoryId,
        );
        return subcategory?.name || "";
      },
      filterParams: {
        // 2. Formatear el término de búsqueda (input del usuario)
        textFormatter: (searchTerm: string) => {
          return searchTerm.toLowerCase();
        },
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const rowData = params.data;

        // Filtrar subcategorías según la categoría seleccionada
        const filteredSubcategories = documentSubcategoryId.filter(
          (subcat) => subcat.categoryId === rowData.documentCategoryId,
        );

        // Verificar si la subcategoría actual es válida
        const isValidSubcategory = filteredSubcategories.some(
          (subcat) => subcat.id === rowData.documentSubcategoryId,
        );

        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newValue = e.target.value ? parseInt(e.target.value, 10) : null;
          params.node.setDataValue("documentSubcategoryId", newValue);

          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[rowData.id]) {
              newChanges[rowData.id] = new Set();
            }
            newChanges[rowData.id].add("documentSubcategoryId");
            return newChanges;
          });
        };

        return (
          <select
            value={rowData.documentSubcategoryId || ""}
            onChange={handleChange}
            disabled={!rowData.documentCategoryId}
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            <option value="" disabled>
              {rowData.documentCategoryId
                ? "Seleccione subcategoría"
                : "Seleccione categoría primero"}
            </option>
            {filteredSubcategories.map((subcat) => (
              <option key={subcat.id} value={subcat.id}>
                {subcat.name}
              </option>
            ))}
            {rowData.documentSubcategoryId && !isValidSubcategory && (
              <option value={rowData.documentSubcategoryId} disabled>
                {documentSubcategoryId.find(
                  (sc) => sc.id === rowData.documentSubcategoryId,
                )?.name || `Subcategoría ${rowData.documentSubcategoryId}`}
              </option>
            )}
          </select>
        );
      },
    },
    {
      field: "documentType",
      headerName: "Tipo de Documento",
      filter: true,
      filterValueGetter: (params) => {
        const value = params.data.documentType;
        if (value === "digital") return "Digital";
        if (value === "physical") return "Físico";
        return value;
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some((f) => f.field === "documentType") &&
          (params.data.documentType === undefined ||
            params.data.documentType === null ||
            params.data.documentType === ""),
      },
      cellRenderer: (params: any) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const updatedValue = e.target.value;
          params.setValue(updatedValue);

          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[params.data.id]) {
              newChanges[params.data.id] = new Set();
            }
            newChanges[params.data.id].add("documentType");
            return newChanges;
          });
        };

        return (
          <select
            value={params.value}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "5px",
              border: "1px solid #ccc",
              borderRadius: "4px",
            }}
          >
            <option value="digital">Digital</option>
            <option value="physical">Físico</option>
          </select>
        );
      },
    },
    {
      field: "typeName",
      headerName: "Tipo de Productor",
      editable: false,
      filter: true,
      width: 150,
      cellRenderer: (params: any) => params.value || "-",
    },
    {
      field: "physicalCopyRequired",
      headerName: "Copia Física Requerida",
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      width: 180,
      cellRenderer: (params: any) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const updatedValue = e.target.checked;
          params.setValue(updatedValue);
          // Mark row as changed
          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[params.data.id]) {
              newChanges[params.data.id] = new Set();
            }
            newChanges[params.data.id].add("physicalCopyRequired");
            return newChanges;
          });
        };

        return (
          <input
            type="checkbox"
            checked={params.value || false}
            onChange={handleChange}
            style={{
              width: "16px",
              height: "16px",
              cursor: "pointer",
            }}
          />
        );
      },
    },
    {
      field: "expirationDate",
      headerName: "Fecha de Vencimiento",
      editable: true,
      filter: true,
      width: 180,
      cellClass: (params: any) => {
        const classes = [];

        // Add change tracking class
        if (changedCells[params.data.id]?.has(params.colDef.field ?? "")) {
          classes.push("cell-changed");
        }

        // Add expired class
        if (isExpired(params.value)) {
          classes.push("cell-expired");
        }

        return classes;
      },
      cellRenderer: (params: any) => {
        const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const updatedValue = e.target.value || null;
          params.setValue(updatedValue);

          setChangedCells((prev) => {
            const newChanges = { ...prev };
            if (!newChanges[params.data.id]) {
              newChanges[params.data.id] = new Set();
            }
            newChanges[params.data.id].add("expirationDate");
            return newChanges;
          });
        };

        const formatDateForInput = (dateString?: string) => {
          if (!dateString) return "";
          try {
            const date = new Date(dateString);
            return date.toISOString().split("T")[0]; // Format as YYYY-MM-DD
          } catch {
            return "";
          }
        };

        return (
          <input
            type="date"
            value={formatDateForInput(params.value)}
            onChange={handleDateChange}
            style={{
              width: "100%",
              padding: "0px",
              border: "0px solid #ccc",
              borderRadius: "0px",
              backgroundColor: "transparent",
            }}
          />
        );
      },
    },
    {
      field: "expirationNoticeDays",
      headerName: "Anticipación de vencimiento (días)",
      editable: true,
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some((f) => f.field === "expirationNoticeDays") &&
          (params.data.expirationNoticeDays === undefined ||
            params.data.expirationNoticeDays === null ||
            params.data.expirationNoticeDays === ""),
      },
    },
    {
      field: "expirationGraceDays",
      headerName: "Tolerancia (días)",
      editable: true,
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some((f) => f.field === "expirationGraceDays") &&
          (params.data.expirationGraceDays === undefined ||
            params.data.expirationGraceDays === null ||
            params.data.expirationGraceDays === ""),
      },
    },
    {
      field: "expirationReminderRepeatDays",
      headerName: "Repetición de alarma de vencimiento (días)",
      editable: true,
      filter: true,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some(
            (f) => f.field === "expirationReminderRepeatDays",
          ) &&
          (params.data.expirationReminderRepeatDays === undefined ||
            params.data.expirationReminderRepeatDays === null ||
            params.data.expirationReminderRepeatDays === ""),
      },
    },

    {
      field: "version",
      headerName: "Versión",
      editable: false,
      filter: true,
      width: 320,
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
        "cell-invalid": (params) =>
          requiredFields.some((f) => f.field === "version") &&
          (params.data.version === undefined ||
            params.data.version === null ||
            params.data.version === ""),
      },
    },
    {
      field: "tags",
      headerName: "Tags",
      filter: "agTextColumnFilter",
      sortable: false,
      resizable: true,
      width: 250,
      filterValueGetter: (params) => {
        const tagNames = (params.data.tags || [])
          .map((tag: Tag) => tag.name)
          .join(", ");
        return tagNames;
      },
      filterParams: {
        textFormatter: (searchTerm: string) => {
          return searchTerm.toLowerCase();
        },
      },
      cellClassRules: {
        "cell-changed": (params) =>
          changedCells[params.data.id]?.has(params.colDef.field ?? ""),
      },
      cellRenderer: (params: any) => {
        const currentTags = params.data.tags || [];
        const rowId = params.data.id;

        const handleTagsChange = (selectedTags: Tag[]) => {
          // Update the documents array directly
          setDocuments((prevDocs) =>
            prevDocs.map((doc) =>
              doc.id === rowId ? { ...doc, tags: selectedTags } : doc,
            ),
          );

          // Mark as changed
          setChangedCells((prev) => {
            const updated = { ...prev };
            if (!updated[rowId]) {
              updated[rowId] = new Set();
            }
            updated[rowId].add("tags");
            return updated;
          });
        };

        return (
          <TagsCell
            currentTags={currentTags}
            allTags={tags}
            onTagsChange={handleTagsChange}
          />
        );
      },
    },

    // Insert additional field columns before the actions column
    ...generateAdditionalFieldColumns(),

    {
      field: "actions",
      headerName: "Acciones",
      editable: false,
      filter: false,
      width: 120,
      cellRenderer: (params: any) => {
        const isTemporary = params.data.id < 0;
        const isDeleting = deletingIds.includes(params.data.id);

        return (
          <div style={{ textAlign: "center" }}>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => confirmDelete(params.data.id)} // Changed from handleDelete to confirmDelete
              disabled={isDeleting}
              title={isTemporary ? "Eliminar fila" : "Eliminar documento"}
              style={{
                padding: "3px 6px",
                minWidth: "30px",
                opacity: isDeleting ? 0.5 : 1,
              }}
            >
              {isDeleting ? (
                <i className="fa fa-spinner fa-spin"></i>
              ) : (
                <i className="fa fa-trash"></i>
              )}
            </button>
          </div>
        );
      },
    },
  ];
  useEffect(() => {
    if (assets.length > 0 && documents.length > 0) {
      const updatedDocuments = documents.map((doc) => {
        const asset = assets.find((a) => a.id === doc.assetId);
        return {
          ...doc,
          typeName: asset?.typeName || "",
        };
      });
      setDocuments(updatedDocuments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets]);

  useEffect(() => {
    if (documents.length > 0) {
      const docWithCategory = documents.find((d) => d.documentCategoryId);
      if (docWithCategory) {
        setSelectedCategoryId(docWithCategory.documentCategoryId ?? null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    if (documents.length > 0 && assets.length > 0) {
      const updatedDocuments = documents.map((doc) => {
        const asset = assets.find((a) => a.id === doc.assetId);
        return {
          ...doc,
          typeName: asset?.typeName || "",
        };
      });

      const hasChanges = updatedDocuments.some(
        (doc, i) => doc.typeName !== documents[i].typeName,
      );
      if (hasChanges) {
        setDocuments(updatedDocuments);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, documents]);

  useEffect(() => {
    const loadData = async () => {
      if (companyId !== undefined) {
        try {
          setLoading(true);
          await Promise.all([
            fetchDocumentCategories(),
            fetchDocumentSubCategories(),
            fetchAssets(),
            fetchSubcompanies(),
          ]);
          await fetchDocuments(); // ← call fetchDocuments after assets
        } catch (error) {
          console.error("Error loading data:", error);
          warningAlert("Error al cargar los datos iniciales");
        } finally {
          setLoading(false);
        }
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, location.search]);

  const onPageChange = (page: number) => {
    setLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    let previousParams = getURLParams();
    urlParams.set(
      "search",
      previousParams.get("search") ? previousParams.get("search") : "",
    );
    urlParams.set("page", page.toString());
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };
  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const onSearch = () => {
    setSearchLoading(true);
    let urlParams = new URLSearchParams(history.location.search);
    urlParams.set("search", search);
    let url = `?${urlParams.toString()}`;
    history.push(url);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault(); // ← esto evita que se dispare cualquier botón con foco
      onSearch();
    }
  };

  const handleDownloadClick = async (downloadDoc: Document) => {
    try {
      if (!downloadDoc || !downloadDoc.id) {
        throw new Error("Documento inválido");
      }

      // Agregar el ID al Set
      setDownloadingIds((prev) => new Set(prev).add(downloadDoc.id!));

      const downloadUrl = getAbsoluteUrl(
        `/documents/${downloadDoc.id}/get_document_url/`,
      );

      const downloadResponse = await fetchAuthenticated(downloadUrl, {
        method: "GET",
      });

      if (!downloadResponse.ok) {
        throw new Error(
          `Error HTTP ${downloadResponse.status}: ${downloadResponse.statusText}`,
        );
      }

      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadDoc.filePath || "documento";

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error al descargar:", error);
      warningAlert(
        `Error al descargar: ${
          error instanceof Error ? error.message : String(error)
        }`,
      );
    } finally {
      // Remover el ID del Set
      setDownloadingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadDoc.id!);
        return newSet;
      });
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get("search") || "";
    const pageParam = parseInt(params.get("page") || "1", 10);

    setSearch(searchParam);
    setCurrentPage(pageParam);
    setSearchLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <CRow>
      <CCol lg="12">
        <CCard>
          <CCardHeader className="p-3">
            {/* Primera fila: contador + botón guardar */}
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
              <div style={{ fontWeight: "bold" }}>
                Cambios a aplicar:{" "}
                {Object.values(changedCells).reduce(
                  (acc, fields) => acc + fields.size,
                  0,
                )}
              </div>

              <CButton
                color="primary"
                onClick={handleSaveChanges}
                disabled={Object.keys(changedCells).length === 0}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <i className="fa fa-save"></i>
                <span>Guardar</span>
              </CButton>
            </div>

            {/* Segunda fila: buscador */}
            <div>
              <CInputGroup>
                <CInputGroupText>
                  <i className="fa fa-search"></i>
                </CInputGroupText>
                <CInput
                  type="text"
                  placeholder="Introduzca el término de búsqueda"
                  onChange={onSearchChange}
                  value={search}
                  onKeyDown={handleKeyDown}
                />
                <CButton
                  type="button"
                  color="primary"
                  variant="outline"
                  onClick={onSearch}
                  disabled={searchLoading}
                >
                  {searchLoading ? (
                    <>
                      <i className="fa fa-spinner fa-spin"></i> Buscando...
                    </>
                  ) : (
                    "Buscar"
                  )}
                </CButton>
              </CInputGroup>
            </div>
          </CCardHeader>

          <CCardBody>
            <div>
              <div
                className="ag-theme-alpine"
                style={{ height: 800, width: "100%", marginBottom: "20px" }}
              >
                <AgGridReact
                  rowData={documents}
                  columnDefs={columns}
                  getRowClass={getRowClass}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    wrapText: true,
                    autoHeaderHeight: true,
                  }}
                  localeText={spanishLocaleText}
                  pagination={true}
                  onCellValueChanged={onCellValueChanged}
                  onGridReady={onGridReady}
                  theme="legacy"
                  stopEditingWhenCellsLoseFocus={true}
                  singleClickEdit={true}
                  suppressClickEdit={false}
                  undoRedoCellEditing={true}
                  undoRedoCellEditingLimit={20}
                />
              </div>
              <div className="d-flex justify-content-start mb-3">
                <CButton
                  type="button"
                  color="success"
                  onClick={handleAddNewRow}
                  disabled={addRowLoading}
                >
                  <i className="fa fa-plus"></i>
                  <span>Agregar Fila (Documento)</span>
                  {addRowLoading && <span className="ml-2">(Cargando...)</span>}
                </CButton>
              </div>
              <div className="d-flex justify-content-center">
                <CPagination
                  pages={totalPages}
                  activePage={currentPage}
                  onActivePageChange={(i: number) => onPageChange(i)}
                  className={totalPages < 2 ? "d-none" : ""}
                />
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
      {/* Versions Modal */}
      {versionsModalVisible && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
          }}
        >
          <div
            className="modal-dialog modal-lg"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              margin: 0,
            }}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Versiones del Documento: {selectedDocument?.name}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseVersionsModal}
                ></button>
              </div>
              <div className="modal-body">
                {versionsLoading ? (
                  <div className="text-center p-3">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">Cargando versiones...</p>
                  </div>
                ) : documentVersions.length === 0 ? (
                  <div className="text-center p-3">
                    <p>No hay versiones disponibles para este documento.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Versión</th>
                          <th>Fecha de Modificación</th>
                          <th>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documentVersions.map((version: any) => (
                          <tr key={version.id}>
                            <td>{version.version || version.id}</td>
                            <td>
                              {version.createdAt
                                ? new Date(version.createdAt).toLocaleString(
                                    "es-ES",
                                  )
                                : "N/A"}
                            </td>
                            <td>
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() =>
                                  handleDownloadVersion(version.version)
                                }
                                title="Descargar esta versión"
                              >
                                <i className="fa fa-download"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseVersionsModal}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </CRow>
  );
};

export default DocumentSpreadsheetCrud;
